import React, { useMemo, ReactNode } from 'react'
import { TableCell } from '@mui/material'
import { Dimension, PriceTypes } from '../../../modules/privateOffer/types'
import { ColumnReadMore, isNonEmptyNumber, numberFormat } from '@labrav/common'
import { get } from 'lodash'
import { useStyles } from './StandardCustomDimensions.styles'
import { TableComponentV2, TextInputV2 } from '@labrav/react-components'
import clsx from 'clsx'
import { FormikProps } from 'formik'

export type MultiPriceObj = {
  keyName: keyof PriceTypes
  lable: string
}

export type CustomColumnObj = {
  keyName: string
  lable: string
}

export type StandardCustomDimensionsProps = {
  dimensions?: Dimension[]
  flexiblePaymentTerms?: boolean
  priceArray?: MultiPriceObj[]
  priceColumnName?: string
  customPriceHeader?: string
  customColumns?: CustomColumnObj[]
  hideDesc?: boolean
  isEditableQuantity?: boolean
  formikProps?: FormikProps<Record<string, unknown>>
  handleChangeQuantity?: (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => void
  currencySymbol?: string
}

export const StandardCustomDimensions = ({
  dimensions,
  flexiblePaymentTerms,
  priceArray,
  priceColumnName,
  customPriceHeader,
  customColumns = [],
  hideDesc = false,
  isEditableQuantity = false,
  formikProps,
  handleChangeQuantity,
  currencySymbol,
}: StandardCustomDimensionsProps) => {
  const { classes } = useStyles()

  const getErrorMsg = (keyName: string) => {
    return ((!!get(formikProps?.touched, keyName, false) &&
      get(formikProps?.errors, keyName, ' ')) ||
      '') as string
  }

  const mappedPrices: MultiPriceObj[] = flexiblePaymentTerms
    ? [{ keyName: 'quantity' as keyof PriceTypes, lable: 'Quantity' }]
    : priceArray
      ? priceArray
      : [{ keyName: 'price', lable: customPriceHeader || 'Customer price' }]

  const dimensionsList = dimensions?.filter(
    dimension => dimension?.type === 'standard' || dimension?.type === 'custom'
  )

  const obj = [...customColumns, ...mappedPrices].reduce<{
    [key: string]: string
  }>((acc, { keyName, lable }) => {
    acc[lable] = keyName
    return acc
  }, {})

  const MetricMapping: Record<string, string> = {
    Dimension: 'label',
    ...(!hideDesc ? { Description: 'desc' } : {}),
    ...obj,
  }

  const totalColumn =
    mappedPrices?.length + customColumns?.length + (hideDesc ? 1 : 2)
  const nameWidth = `${100 / totalColumn - (hideDesc ? 0 : 2)}%`
  const priceWidth = `${100 / totalColumn - (hideDesc ? 0 : 8)}%`

  const customHeaderMapping = useMemo(() => {
    let customHeader = {} as Record<string, ReactNode>
    Object.keys(MetricMapping).forEach((key, index) => {
      customHeader = {
        ...customHeader,
        [key]: (
          <TableCell
            className={clsx(
              classes.tableHead,
              index >= (hideDesc ? 1 : 2) ? classes.titleRight : ''
            )}
            style={{
              width:
                index === 0 ? nameWidth : index !== 1 ? priceWidth : 'auto',
            }}
            key={`header-${MetricMapping[key]}-index`}
            data-testid={`header-${MetricMapping[key]}`}
          >
            {key}
          </TableCell>
        ),
      }
    })
    return customHeader
  }, [MetricMapping])

  const metricMappingDataObjects = (dimensionsList || []).map(
    (dimension, dimensionIndex) => {
      const obj = mappedPrices.reduce<{ [key: string]: JSX.Element }>(
        (acc, { keyName }) => {
          const keyNameInfo = priceColumnName
            ? `${priceColumnName ? `${priceColumnName}.` : ''}${keyName}`
            : keyName
          const priceValue = get(dimension, keyNameInfo)
          acc[keyName] = (
            <div
              className={classes.price}
              data-testid={'dimension-price-value'}
            >
              {isNonEmptyNumber(get(dimension, keyNameInfo) as number) ? (
                <>
                  <span className={classes.currency}>
                    {flexiblePaymentTerms ? '' : `${currencySymbol}`}{' '}
                  </span>{' '}
                  {numberFormat(priceValue as number, 3)}
                </>
              ) : (
                '-'
              )}
            </div>
          )

          return acc
        },
        {}
      )

      const customColumnValues = customColumns.reduce<{
        [key: string]: JSX.Element
      }>((acc, { keyName }) => {
        acc[keyName] =
          isEditableQuantity &&
          keyName === 'quantity' &&
          handleChangeQuantity &&
          formikProps ? (
            <div className={classes.editableAmount}>
              <div className={classes.amountInput}>
                <TextInputV2
                  keyName={`dimensions.[${dimensionIndex}].${keyName}`}
                  title={''}
                  type="text"
                  placeHolder=""
                  value={
                    isNonEmptyNumber(get(dimension, keyName))
                      ? get(dimension, keyName, '')
                      : ''
                  }
                  updatedValue={
                    isNonEmptyNumber(get(dimension, keyName))
                      ? get(dimension, keyName, '')
                      : ''
                  }
                  isError={
                    !!(
                      get(
                        formikProps?.touched,
                        `dimensions.[${dimensionIndex}].${keyName}`,
                        false
                      ) &&
                      get(
                        formikProps?.errors,
                        `dimensions.[${dimensionIndex}].${keyName}`,
                        false
                      )
                    )
                  }
                  onChange={e => handleChangeQuantity(e, dimensionIndex)}
                  isReadOnly={false}
                  isRequired={false}
                  errorText={getErrorMsg(
                    `dimensions.[${dimensionIndex}].${keyName}`
                  )}
                  showSpinner={false}
                  onBlur={formikProps?.handleBlur}
                />
              </div>
            </div>
          ) : (
            <div
              className={classes.price}
              data-testid={'dimension-price-value'}
            >
              {isNonEmptyNumber(get(dimension, keyName) as number) ? (
                <>
                  {keyName !== 'quantity' && (
                    <span className={classes.currency}>{currencySymbol}</span>
                  )}
                  {numberFormat(get(dimension, keyName) as number, 3)}
                </>
              ) : (
                '-'
              )}
            </div>
          )

        return acc
      }, {})
      return {
        label: (
          <div className={classes.cellText}>
            {dimension?.label ? (
              <ColumnReadMore
                customCharForRemoveFromMax={15}
                description={dimension?.label}
              />
            ) : (
              '-'
            )}
          </div>
        ),
        ...(!hideDesc
          ? {
              desc: dimension?.labelDescription ? (
                <div className={classes.cellText}>
                  <ColumnReadMore
                    customCharForRemoveFromMax={15}
                    description={dimension?.labelDescription}
                  />
                </div>
              ) : (
                '-'
              ),
            }
          : {}),
        ...obj,
        ...customColumnValues,
      }
    }
  )

  return (
    <div className={classes.root} data-testid={'standard-custom-dimesnions'}>
      <TableComponentV2
        objects={metricMappingDataObjects}
        mapping={MetricMapping}
        customHeader={customHeaderMapping}
        count={metricMappingDataObjects.length}
        fetchMoreData={() => void 0}
        onChangeCheckbox={() => void 0}
        selectedItem={[]}
        showCheckbox={false}
        isBasicTable={true}
        keyName="table-basic"
      />
    </div>
  )
}
